import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';

import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import { IconChevronsLeft, IconChevronsRight } from '@tabler/icons';
import useAuth from 'hooks/useAuth';
import useInvoices from 'hooks/useInvoices';
import useSignature from 'hooks/useSignature';
import useUser from 'hooks/useUser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Rnd } from 'react-rnd';
import { usePrevious } from 'react-use';
import MainCard from 'views/components/cards/MainCard';
import CenteredLoading from 'views/components/CenteredLoading';
import NoResults from 'views/components/common/NoResults';
import PreviewsNoteSession from './PreviewsNoteSession';
import SessionNoteForm from './SessionNoteForm';
import { SessionData } from './useSessionData';
import useSessionNotes from './useSessionNotes';

interface Props {
  sessionData: SessionData;
  onClose: () => void;
}

export default function SessionNotes({ sessionData, onClose }: Props) {
  const intl = useIntl();
  const notesRef = useRef<HTMLDivElement>(null);

  const { userId, request } = useAuth();
  const { isClient } = useUser(userId);
  const { notes, refetchNotes } = useSessionNotes(sessionData.appointmentId, userId);
  const previousNotes = usePrevious(notes);
  const [currentNotes, setCurrentNotes] = useState(notes);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [notesHidden, setNotesHidden] = useState(true);
  const [notesWidth, setNotesWidth] = useState(1120);
  const [notesHeight, setNotesHeight] = useState(590);
  const [showPreviousNotes, setShowPreviousNotes] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>('ALL_PARTICIPANTS');
  const previousParticipantId = usePrevious(selectedParticipantId);
  const [filteredSessionData, setFilteredSessionData] = useState(sessionData);
  const [activeParticipantsNotes, setActiveParticipantsNotes] = useState('All Participants');

  const [leftPanelOpen, setLeftPanelOpen] = useState(true);

  const handleViewPreviousNotes = () => {
    setShowPreviousNotes(true);
  };

  const handleClosePreviousNotes = () => {
    setShowPreviousNotes(false);
  };
  const closeHandler = () => {
    onClose();
  };

  const { invoiceSettings } = useInvoices({ userId }) || {};
  const { binarySignature } = useSignature({ userId, enabled: invoiceSettings?.hasSignature });

  useEffect(() => {
    if (!notes?.length) {
      setSelectedNoteId(null);
      return;
    }

    const sortedNotes = [...notes].sort((a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime());

    if (previousNotes && sortedNotes.length > previousNotes.length) {
      setSelectedNoteId(sortedNotes.slice(-1)[0]._id);
    } else {
      if (previousParticipantId === selectedParticipantId) {
        setSelectedNoteId((prevId) => sortedNotes.find((note) => note?._id === prevId)?._id ?? null);
      }
    }
  }, [notes, previousNotes, selectedParticipantId, previousParticipantId]);

  useEffect(() => {
    const providers = sessionData.participants.filter((participant) => participant?.user?.role !== 'CLIENT');

    let addedNoteParticipants;
    if (selectedParticipantId === 'ALL_PARTICIPANTS') {
      addedNoteParticipants = sessionData.participants.filter((participant) => participant?.user?.role === 'CLIENT');
    } else {
      addedNoteParticipants = sessionData.participants.filter((participant) => participant?.user?._id === selectedParticipantId);
    }

    const selectedParticipants = [...providers, ...addedNoteParticipants];

    setFilteredSessionData({
      ...sessionData,
      participants: selectedParticipants
    });
  }, [selectedParticipantId, sessionData]);

  const handleParticipantClick = (participantId: string) => {
    setSelectedNoteId(null);
    setSelectedParticipantId(participantId);
  };
  const handleNotes = (participant?) => {
    const userId = participant?.user?._id;

    const noOfParticpiants = sessionData?.participants.length;
    let filteredNotes;
    if (participant === 'all_participants') {
      setActiveParticipantsNotes(`All Participants`);

      filteredNotes = notes?.filter((notes) => {
        return notes?.participants.length === noOfParticpiants;
      });
    } else {
      setActiveParticipantsNotes(`${participant?.user?.firstName} ${participant?.user?.lastName}`);

      if (noOfParticpiants === 2) {
        filteredNotes = notes?.filter((notes) => {
          return notes?.participants.length === noOfParticpiants;
        });
      } else {
        filteredNotes = notes
          ?.filter((note) => note?.participants.length !== noOfParticpiants)
          ?.filter((note) => note?.participants.includes(userId));
      }
    }
    setCurrentNotes(filteredNotes);
    setNotesHidden(!notesHidden);
  };

  useEffect(() => {
    if (currentNotes && currentNotes.length > 0) {
      setSelectedNoteId(currentNotes.slice(-1)[0]._id);
    }
  }, [currentNotes]);

  const renderNoteListForClient = useCallback(() => {
    if (!notes) {
      return <CenteredLoading />;
    }

    if (!notes.length) {
      return (
        <NoResults>
          <FormattedMessage defaultMessage="No notes found" />
        </NoResults>
      );
    }

    return (
      <Box
        sx={{
          overflowY: 'scroll',
          height: notesHeight * 0.8,
          paddingBottom: 5
        }}
        m={0}
        p={0}
      >
        <List disablePadding sx={{ pb: 4 }}>
          {notes?.map((note) => (
            <ListItemButton key={note?._id} selected={note?._id === selectedNoteId} onClick={() => setSelectedNoteId(note?._id)}>
              <ListItemText primary={note?.title} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
  }, [notes, selectedNoteId, setSelectedNoteId, notesHeight]);

  const renderNoteList = useCallback(() => {
    if (!currentNotes) {
      return <CenteredLoading />;
    }

    if (!currentNotes.length) {
      return (
        <NoResults>
          <FormattedMessage defaultMessage="No notes found" />
        </NoResults>
      );
    }
    return (
      <Box
        sx={{
          overflowY: 'scroll',
          height: notesHeight * 0.8,
          paddingBottom: 5
        }}
        m={0}
        p={0}
      >
        <List disablePadding sx={{ pb: 4 }}>
          {currentNotes?.map((note) => (
            <ListItemButton key={note?._id} selected={note?._id === selectedNoteId} onClick={() => setSelectedNoteId(note?._id)}>
              <ListItemText primary={note?.title} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNotes, selectedNoteId, setSelectedNoteId, notesHeight, notes]);

  const handleClearNotes = () => {
    setSelectedNoteId(null);
    setNotesHidden(true);
  };
  const extractNameFromEmail = (email: string) => {
    const regex = /(^[^@]*)@/;
    const match = email.match(regex);
    return match ? match[1] : 'Unknown';
  };
  return (
    <Rnd
      default={{
        x: 250,
        y: 50,
        width: notesWidth,
        height: notesHeight
      }}
      minWidth={660}
      minHeight={590}
      style={{ zIndex: 1100 }}
      onResize={(e, direction, ref, delta, position) => {
        setNotesWidth(ref.offsetWidth);
        setNotesHeight(ref.offsetHeight);
      }}
      cancel=".main-editor-custom"
    >
      <Box>
        {!showPreviousNotes ? (
          <Box
            sx={{
              width: notesWidth,
              height: notesHeight
            }}
          >
            <>
              <Box
                style={{
                  backgroundColor: 'white',
                  height: '100%',
                  border: '1px solid gray'
                }}
                sx={(theme) => ({ borderRadius: theme.shape.borderRadius })}
              >
                <Grid container item xs={12}>
                  <Grid item xs={6}>
                    <Box sx={{ py: 3, px: 3 }}>
                      <Typography variant="h2">
                        {' '}
                        <FormattedMessage defaultMessage="Add Note" />
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', direction: 'rtl' }}>
                    <IconButton onClick={onClose} aria-label="close">
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Grid>

                <Tooltip
                  title={
                    leftPanelOpen
                      ? intl.formatMessage({ defaultMessage: 'Hide side panel' })
                      : intl.formatMessage({ defaultMessage: 'Show side panel' })
                  }
                >
                  <IconButton
                    sx={{ position: 'absolute', left: -8, top: 120 }}
                    color="primary"
                    onClick={() => setLeftPanelOpen((pre) => !pre)}
                  >
                    {!leftPanelOpen ? <IconChevronsRight size={24} /> : <IconChevronsLeft size={24} />}
                  </IconButton>
                </Tooltip>

                {leftPanelOpen && (
                  <Grid container item xs={12} sx={{ px: 3.7 }}>
                    <Grid item xs={3}>
                      {!isClient && (
                        <Typography variant="h4">
                          <FormattedMessage defaultMessage="Attendees" />
                        </Typography>
                      )}
                    </Grid>
                    {!isClient && (
                      <Grid item xs={4}>
                        <Typography variant="h4" style={{ paddingLeft: '8px' }}>
                          <FormattedMessage defaultMessage="Template" />
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={isClient ? 9 : 5} sx={{ px: 0.7 }}>
                      <Typography variant="h4" style={{ paddingLeft: isClient ? 0 : '56px' }}>
                        <FormattedMessage defaultMessage="Title" />
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                <Grid container spacing={3} sx={{ px: 3, py: 1, height: notesHeight - 142 }}>
                  {!notesHidden && (
                    <Grid ref={notesRef} item md={notesHidden ? 0 : 3} style={{ height: '100%' }}>
                      <MainCard
                        sx={{ height: '100%' }}
                        title={
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="h6">{activeParticipantsNotes}</Typography>
                          </Stack>
                        }
                        headerSX={{ padding: 2 }}
                        contentSX={{ padding: notes?.length ? 0 : 0, height: '100%' }}
                      >
                        <Box sx={{ display: 'flex', p: 0, justifyContent: 'flex-end' }}>
                          <Tooltip title={intl.formatMessage({ defaultMessage: 'Hide Notes' })}>
                            <IconButton color="primary" onClick={() => setNotesHidden(!notesHidden)}>
                              <IconChevronsLeft size={24} />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {renderNoteList()}
                      </MainCard>
                    </Grid>
                  )}
                  {notesHidden && leftPanelOpen && !isClient && (
                    <Grid ref={notesRef} item md={3} style={{ height: '100%' }}>
                      <Stack>
                        <MainCard sx={{ height: '100%' }} headerSX={{ padding: 2 }} contentSX={{ padding: 0, height: '100%' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              py: 1,
                              px: 1,
                              justifyContent: 'flex-start',
                              overflow: 'hidden',
                              maxWidth: '100%',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            <Tooltip title={<FormattedMessage defaultMessage="Select attendees to assign notes" />} arrow placement="top">
                              <Typography
                                variant="h6"
                                fontWeight="light"
                                sx={{
                                  paddingLeft: '8px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <FormattedMessage defaultMessage="Select attendees to assign notes" />
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              height: notesHeight - 245,
                              justifyContent: 'space-between'
                            }}
                            m={0}
                            p={0}
                          >
                            <Box
                              sx={{
                                flexGrow: 1
                              }}
                            >
                              <List disablePadding sx={{ pb: 4 }}>
                                {sessionData.participants.filter((participant) => participant?.user?.role === 'CLIENT').length > 1 && (
                                  <ListItemButton
                                    selected={selectedParticipantId === 'ALL_PARTICIPANTS'}
                                    onClick={() => handleParticipantClick('ALL_PARTICIPANTS')}
                                  >
                                    <ListItemAvatar>
                                      <Avatar sx={{ width: 40, height: 40 }} />
                                    </ListItemAvatar>
                                    <ListItemText primary={<FormattedMessage defaultMessage="All Participants" />} />{' '}
                                    <Tooltip title={intl.formatMessage({ defaultMessage: 'Show this session Notes' })}>
                                      <IconButton color="primary" onClick={() => handleNotes('all_participants')}>
                                        {notesHidden ? <IconChevronsRight size={24} /> : <IconChevronsLeft size={24} />}
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemButton>
                                )}
                                {sessionData.participants
                                  .filter((participant) => participant?.user?.role === 'CLIENT')
                                  .map((participant) => (
                                    <ListItemButton
                                      key={participant?.user?._id}
                                      selected={participant?.user?._id === selectedParticipantId}
                                      onClick={() => handleParticipantClick(participant.user._id)}
                                    >
                                      <ListItemAvatar>
                                        <Avatar
                                          src={participant?.user?.imageUrl}
                                          alt={`${participant?.user?.firstName} ${participant.user.lastName}`}
                                        />
                                      </ListItemAvatar>
                                      <ListItemText primary={`${participant?.user?.firstName} ${participant?.user?.lastName}`} />
                                      <Tooltip title={intl.formatMessage({ defaultMessage: 'Show this session Notes' })}>
                                        <IconButton color="primary" onClick={() => handleNotes(participant)}>
                                          {notesHidden ? <IconChevronsRight size={24} /> : <IconChevronsLeft size={24} />}
                                        </IconButton>
                                      </Tooltip>
                                    </ListItemButton>
                                  ))}
                                {sessionData?.floatingParticipants.length > 0 &&
                                  sessionData?.floatingParticipants.map((participant) => (
                                    <ListItemButton
                                      key={participant?._id}
                                      selected={participant?._id === selectedParticipantId}
                                      onClick={() => handleParticipantClick(participant?._id)}
                                    >
                                      <ListItemAvatar>
                                        <Avatar>
                                          <PersonIcon />
                                        </Avatar>
                                      </ListItemAvatar>
                                      <ListItemText primary={`${extractNameFromEmail(participant.email)} (N)`} />
                                    </ListItemButton>
                                  ))}
                              </List>
                            </Box>
                          </Box>
                        </MainCard>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Button variant="outlined" sx={{ width: '100%', lineHeight: 1.1 }} onClick={handleViewPreviousNotes}>
                            <AccessTimeIcon /> <FormattedMessage defaultMessage="View Previous Notes" />
                          </Button>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {isClient && leftPanelOpen && (
                    <Grid ref={notesRef} item md={3} style={{ height: '100%' }}>
                      <MainCard
                        sx={{ height: '100%' }}
                        title={''}
                        headerSX={{ padding: 2 }}
                        contentSX={{ padding: notes?.length ? 0 : 3, height: '100%' }}
                      >
                        {renderNoteListForClient()}
                      </MainCard>
                    </Grid>
                  )}
                  <Grid item md={leftPanelOpen ? 9 : 12} style={{ height: '100%' }}>
                    <SessionNoteForm
                      key={selectedNoteId}
                      noteId={selectedNoteId}
                      sessionData={sessionData}
                      filterSessionData={filteredSessionData}
                      onSubmitSuccess={refetchNotes}
                      onClose={onClose}
                      notesHeight={notesHeight}
                      binarySignature={binarySignature}
                      selectedParticipantId={selectedParticipantId}
                      clearNotes={handleClearNotes}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          </Box>
        ) : (
          <PreviewsNoteSession sessionData={sessionData} onClose={handleClosePreviousNotes} onCompleteClose={closeHandler} />
        )}
      </Box>
    </Rnd>
  );
}
